import React, { useState, useEffect, useCallback } from 'react';
import { UserCog, Plus, Edit2, Loader2, RefreshCw, Power, AlertCircle, MapPin, X } from 'lucide-react';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { fleetToast } from '../../utils/toast';
import { FLEET_PERMISSION } from '../../utils/fleetPermissions';
import SupervisorFormModal from './SupervisorFormModal';
import './SupervisorsPage.css';

/** Maskedeki set bit sayısı — tanımlı yetki bayraklarına göre sayılır, ham ikili basamak sayılmaz. */
const permissionCount = (mask) => Object.values(FLEET_PERMISSION).filter(f => (Number(mask || 0) & f) === f).length;

const AREA_BADGE_LIMIT = 3;

const SupervisorsPage = () => {
    const [supervisors, setSupervisors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [formModal, setFormModal] = useState(null); // null | { mode: 'create' } | { mode: 'edit', supervisor }
    const [toggleTarget, setToggleTarget] = useState(null);
    const [toggling, setToggling] = useState(false);

    const loadSupervisors = useCallback(async () => {
        try {
            setLoading(true);
            const res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.SUPERVISORS_LIST, { page, pageSize });
            if (res.success) {
                setSupervisors(res.data?.items || []);
                const total = res.data?.totalCount || 0;
                setTotalCount(total);
                setTotalPages(Math.max(1, Math.ceil(total / pageSize)));
            } else {
                fleetToast.error(res.error || 'Sorumlu listesi alınamadı');
            }
        } catch {
            fleetToast.error('Sorumlu listesi alınamadı');
        } finally {
            setLoading(false);
        }
    }, [page, pageSize]);

    useEffect(() => { loadSupervisors(); }, [loadSupervisors]);

    const handleSaved = () => {
        setFormModal(null);
        loadSupervisors();
    };

    const handleConfirmToggle = async () => {
        if (!toggleTarget || toggling) return;
        setToggling(true);
        try {
            const nextActive = !toggleTarget.isActive;
            // PUT (PATCH DEĞİL): Cloudflare WAF PATCH isteklerini bloklar, backend ucu da PUT'a taşındı.
            const res = await apiService.put(API_ENDPOINTS.FLEET_PANEL.SUPERVISOR_ACTIVE(toggleTarget.id, nextActive));
            if (res.success) {
                fleetToast.success(nextActive ? 'Sorumlu aktifleştirildi' : 'Sorumlu pasife alındı');
                setToggleTarget(null);
                await loadSupervisors();
            } else {
                fleetToast.error(res.error || 'İşlem başarısız');
            }
        } catch {
            fleetToast.error('İşlem sırasında hata oluştu');
        } finally {
            setToggling(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('tr-TR', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    return (
        <div className="fm-container sv-container">
            <div className="page-header">
                <div className="header-left">
                    <div className="page-icon"><UserCog size={20} /></div>
                    <div className="page-title">
                        <h1>Bölge Sorumluları</h1>
                        <p>Filonuz adına belirli bölgeleri yönetecek sorumlular tanımlayın <span className="count-badge">{totalCount}</span></p>
                    </div>
                </div>
                <div className="header-right">
                    <button className="btn btn-secondary" onClick={loadSupervisors} disabled={loading}>
                        <RefreshCw size={16} className={loading ? 'spinning' : ''} /> Yenile
                    </button>
                    <button className="btn btn-primary" onClick={() => setFormModal({ mode: 'create' })}>
                        <Plus size={16} /> Yeni Sorumlu
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="page-loading"><Loader2 className="spinning" size={40} /><p>Yükleniyor...</p></div>
            ) : supervisors.length === 0 ? (
                <div className="page-empty">
                    <UserCog size={48} />
                    <h3>Henüz bölge sorumlusu eklenmemiş</h3>
                    <p>Bölge sorumluları belirli bölgelerdeki teslimatları, kuryeleri ve hakedişleri sizin adınıza yönetebilir.</p>
                    <button className="btn btn-primary" onClick={() => setFormModal({ mode: 'create' })}>
                        <Plus size={16} /> İlk Sorumluyu Ekle
                    </button>
                </div>
            ) : (
                <>
                    <div className="data-table-wrapper">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Ad Soyad</th>
                                    <th>Telefon</th>
                                    <th>Bölgeler</th>
                                    <th>Yetki</th>
                                    <th>Durum</th>
                                    <th>İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {supervisors.map((s) => (
                                    <tr key={s.id}>
                                        <td className="cell-name">{s.displayName || '-'}</td>
                                        <td>{s.phoneNumber || '-'}</td>
                                        <td>
                                            {(s.areas || []).length === 0 ? (
                                                <span className="cell-muted">Bölge atanmamış</span>
                                            ) : (
                                                <div className="sv-area-badges">
                                                    {s.areas.slice(0, AREA_BADGE_LIMIT).map((a, i) => (
                                                        <span key={i} className="sv-area-badge">
                                                            <MapPin size={11} />{a.displayName}
                                                        </span>
                                                    ))}
                                                    {s.areas.length > AREA_BADGE_LIMIT && (
                                                        <span className="sv-area-badge sv-area-badge-more">
                                                            +{s.areas.length - AREA_BADGE_LIMIT}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td><span className="count-badge">{permissionCount(s.permissions)} yetki</span></td>
                                        <td><span className={`status-badge ${s.isActive ? 'active' : 'inactive'}`}>{s.isActive ? 'Aktif' : 'Pasif'}</span></td>
                                        <td>
                                            <div className="fm-actions">
                                                <button className="action-btn edit" title="Düzenle" onClick={() => setFormModal({ mode: 'edit', supervisor: s })}>
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    className={`action-btn ${s.isActive ? 'delete' : 'success'}`}
                                                    title={s.isActive ? 'Pasife al' : 'Aktifleştir'}
                                                    onClick={() => setToggleTarget(s)}
                                                >
                                                    <Power size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="simple-pagination">
                        <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Önceki</button>
                        <span>Sayfa {page} / {totalPages}</span>
                        <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Sonraki</button>
                    </div>
                </>
            )}

            {formModal && (
                <SupervisorFormModal
                    mode={formModal.mode}
                    supervisor={formModal.supervisor}
                    onClose={() => setFormModal(null)}
                    onSaved={handleSaved}
                />
            )}

            {toggleTarget && (
                <div className="modal-overlay" onClick={() => !toggling && setToggleTarget(null)}>
                    <div className="modal sv-modal-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{toggleTarget.isActive ? 'Sorumluyu Pasife Al' : 'Sorumluyu Aktifleştir'}</h2>
                            <button className="modal-close" onClick={() => setToggleTarget(null)} disabled={toggling}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="sv-confirm-warn">
                                <AlertCircle size={36} />
                                <p>
                                    {toggleTarget.isActive
                                        ? <><strong>{toggleTarget.displayName}</strong> adlı sorumluyu pasife almak istediğinize emin misiniz? Pasif sorumlu panele giriş yapamaz.</>
                                        : <><strong>{toggleTarget.displayName}</strong> adlı sorumluyu tekrar aktifleştirmek istediğinize emin misiniz?</>}
                                </p>
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={() => setToggleTarget(null)} disabled={toggling}>Vazgeç</button>
                            <button className={`btn ${toggleTarget.isActive ? 'btn-danger' : 'btn-success'}`} onClick={handleConfirmToggle} disabled={toggling}>
                                {toggling ? 'İşleniyor...' : (toggleTarget.isActive ? 'Pasife Al' : 'Aktifleştir')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupervisorsPage;
